import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { fetchInviteQrCode, fetchTeamList } from "@/api/invite";
import type { QrCodeData, TeamMember } from "@/api/invite";

export default function InvitePage() {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  // 二维码数据
  const [qrData, setQrData] = useState<QrCodeData | null>(null);
  const [qrLoading, setQrLoading] = useState(true);
  const [qrError, setQrError] = useState("");

  // 邀请记录（团队列表）
  const [teamList, setTeamList] = useState<TeamMember[]>([]);
  const [teamTotal, setTeamTotal] = useState(0);
  const [teamLoading, setTeamLoading] = useState(true);

  // 页面加载时获取数据
  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    // 并行请求二维码和团队列表
    try {
      const [qrRes, teamRes] = await Promise.allSettled([
        fetchInviteQrCode(),
        fetchTeamList({ page: 1, limit: 50 }),
      ]);

      if (qrRes.status === "fulfilled") {
        setQrData(qrRes.value);
      } else {
        console.error("获取邀请二维码失败:", qrRes.reason);
        setQrError("加载二维码失败，请重试");
      }

      if (teamRes.status === "fulfilled") {
        setTeamList(teamRes.value.list || []);
        setTeamTotal(teamRes.value.total || 0);
      } else {
        console.error("获取推广团队列表失败:", teamRes.reason);
      }
    } finally {
      setQrLoading(false);
      setTeamLoading(false);
    }
  }

  const handleCopyCode = async () => {
    if (!qrData?.invite_code) return;
    try {
      await navigator.clipboard.writeText(qrData.invite_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback：兼容不支持 clipboard API 的环境
      const input = document.createElement("input");
      input.value = qrData.invite_code;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="min-h-screen pb-10" style={{ background: "#F8F9FC", fontFamily: "'Inter','SF Pro Text',sans-serif" }}>
      {/* ====== Header ====== */}
      <div className="bg-white px-5 pb-4 flex items-center gap-3" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 48px)" }}>
        <button onClick={() => navigate(-1)} className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer" style={{ background: "#F3F4F6" }}>
          <i className="ri-arrow-left-line" style={{ fontSize: 20, color: "#374151" }} />
        </button>
        <h1 className="text-lg font-bold text-gray-800">邀请好友</h1>
      </div>

      {/* ====== Hero Banner ====== */}
      <div className="mx-5 mt-4 rounded-3xl overflow-hidden" style={{ background: "linear-gradient(135deg,#1E40AF 0%,#2563EB 45%,#D97706 100%)" }}>
        <div className="px-6 py-9 text-center">
          <div className="text-[42px] mb-2">&#127873;</div>
          <div className="text-2xl font-bold text-white mb-1 tracking-wide">邀好友，得奖励</div>
          <div className="text-white/85 text-sm">每成功邀请1位好友注册并首次下单</div>
        </div>
      </div>

      {/* ====== 二维码区域 ====== */}
      <div className="mx-5 mt-4 bg-white rounded-2xl p-6" style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.04)" }}>
        <div className="text-sm font-semibold text-gray-800 text-center mb-4">我的邀请二维码</div>

        {qrError ? (
          /* 错误状态 */
          <div className="flex flex-col items-center py-8">
            <i className="ri-error-warning-line text-3xl mb-2" style={{ color: "#EF4444" }} />
            <p className="text-xs text-gray-400">{qrError}</p>
            <button
              onClick={() => { setQrError(""); setQrLoading(true); loadData(); }}
              className="mt-3 px-5 py-2 rounded-full text-xs font-semibold text-white"
              style={{ background: "#1E3A8A" }}
            >
              重新加载
            </button>
          </div>
        ) : qrLoading ? (
          /* Loading 状态 */
          <div className="flex flex-col items-center py-12 gap-3">
            <div className="w-40 h-40 rounded-xl border-2 border-dashed flex items-center justify-center" style={{ borderColor: "#93C5FD", background: "#F8FAFC" }}>
              <i className="ri-loader-4-line animate-spin text-3xl" style={{ color: "#2563EB" }} />
            </div>
            <p className="text-xs text-gray-400">正在生成邀请二维码...</p>
          </div>
        ) : (
          <>
            {/* QR Code 容器 */}
            <div className="flex justify-center">
              <div
                className="relative p-3 rounded-xl"
                style={{
                  border: "2px dashed #93C5FD",
                  background: "#FFFFFF",
                }}
              >
                {/* 真实二维码图片 — 使用接口返回的 base64 */}
                <img
                  src={
                    qrData?.qrcode_base64?.startsWith("data:")
                      ? qrData.qrcode_base64
                      : qrData?.qrcode_url
                      ? qrData.qrcode_url
                      : qrData?.qrcode_base64
                        ? `data:image/png;base64,${qrData.qrcode_base64}`
                        : ""
                  }
                  alt="邀请二维码"
                  className="w-[120px] h-[120px]"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
                {/* 二维码中心小 logo */}
                {qrData?.avatar ? (
                  <img
                    src={qrData.avatar}
                    alt=""
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-7 h-7 rounded-full object-cover object-top"
                    style={{ boxShadow: "0 2px 8px rgba(255,255,255,.9), 0 2px 8px rgba(37,99,235,.35)", border: "2px solid #fff" }}
                  />
                ) : (
                  <div
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center"
                    style={{ background: "#2563EB", boxShadow: "0 2px 8px rgba(37,99,235,.35)" }}
                  >
                    <i className="ri-gift-line text-white text-sm" />
                  </div>
                )}
              </div>
            </div>

            {/* 提示文字 + 邀请码 */}
            <p className="text-center text-xs text-gray-400 mt-4 leading-relaxed">
              扫一扫上方二维码，或将二维码保存分享给好友
            </p>

            {/* 邀请码展示 & 复制 */}
            {qrData?.invite_code && (
              <div className="mt-3 flex items-center justify-center gap-2">
                <span className="text-xs text-gray-500">我的邀请码：</span>
                <span className="text-sm font-bold tracking-widest" style={{ color: "#1E3A8A" }}>{qrData.invite_code}</span>
                <button
                  onClick={handleCopyCode}
                  className="ml-1 px-2.5 py-0.5 rounded-md text-xs font-medium transition-all active:scale-95 cursor-pointer"
                  style={{
                    background: copied ? "#D1FAE5" : "#EEF2FF",
                    color: copied ? "#059669" : "#4F46E5",
                  }}
                >
                  {copied ? "已复制 ✓" : "复制"}
                </button>
              </div>
            )}

            {/* 长按保存提示 */}
            <div
              className="w-full mt-4 py-3 rounded-full flex items-center justify-center gap-1.5"
              style={{ background: "#F0F4FF" }}
            >
              <i className="ri-hand-coin-line" style={{ fontSize: 15, color: "#6366F1" }} />
              <span className="text-sm font-medium" style={{ color: "#6366F1" }}>
                长按二维码图片即可保存到相册
              </span>
            </div>
          </>
        )}
      </div>

      {/* ====== 如何邀请 ====== */}
      <div className="mx-5 mt-4 bg-white rounded-2xl p-5" style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.04)" }}>
        <div className="text-sm font-semibold text-gray-800 mb-4">如何邀请</div>
        <div className="space-y-4">
          {[
            {
              step: "STEP 01",
              title: "保存邀请二维码",
              desc: '点击上方【保存二维码】，将图片分享给好友',
              iconBg: "#EEF2FF",
              iconColor: "#4F46E5",
              icon: "ri-qr-code-line",
              stepColor: "#4F46E5",
            },
            {
              step: "STEP 02",
              title: "好友扫码注册",
              desc: "好友扫描二维码，接受邀请完成注册并首次下单",
              iconBg: "#FAE8FF",
              iconColor: "#A855F7",
              icon: "ri-user-add-line",
              stepColor: "#A855F7",
            },
          ].map((item) => (
            <div key={item.step} className="flex gap-3">
              <div
                className="w-10 h-10 flex-shrink-0 rounded-xl flex items-center justify-center mt-0.5"
                style={{ background: item.iconBg }}
              >
                <i className={item.icon} style={{ color: item.iconColor, fontSize: 20 }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-xs font-bold" style={{ color: item.stepColor }}>{item.step}</span>
                  <span className="text-sm font-semibold text-gray-800">{item.title}</span>
                </div>
                <p className="text-xs text-gray-400 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ====== 邀请记录 ====== */}
      <div className="mx-5 mt-4 bg-white rounded-2xl p-5" style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.04)" }}>
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-semibold text-gray-800">邀请记录</span>
          {!teamLoading && <span className="text-xs text-gray-400">已邀请 {teamTotal} 人</span>}
        </div>

        {teamLoading ? (
          /* Loading */
          <div className="py-8 space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-center gap-3 animate-pulse">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-24 rounded bg-gray-100" />
                  <div className="h-3 w-20 rounded bg-gray-50" />
                </div>
                <div className="space-y-2">
                  <div className="h-4 w-14 rounded bg-gray-100" />
                  <div className="h-3 w-12 rounded bg-gray-50" />
                </div>
              </div>
            ))}
          </div>
        ) : teamList.length === 0 ? (
          /* 空状态 */
          <div className="py-10 flex flex-col items-center gap-2">
            <i className="ri-team-line text-4xl" style={{ color: "#D1D5DB" }} />
            <p className="text-sm text-gray-400">暂无邀请记录</p>
            <p className="text-xs text-gray-300">分享邀请码给好友，即可查看记录</p>
          </div>
        ) : (
          /* 数据列表 */
          <div className="space-y-4">
            {teamList.map((member) => (
              <div key={member.uid} className="flex items-center gap-3">
                {/* 头像 */}
                <div className="w-10 h-10 rounded-full flex-shrink-0 overflow-hidden bg-gray-100">
                  {member.avatar ? (
                    <img src={member.avatar} alt={member.nickname} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-sm font-medium text-gray-400">
                      {member.nickname.charAt(member.nickname.length - 1)}
                    </div>
                  )}
                </div>
                {/* 名字 + 时间 */}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-700 truncate">{member.nickname || `用户${member.uid}`}</div>
                  <div className="text-xs text-gray-400">{formatDate(member.spread_time)}</div>
                </div>
                {/* 手机号脱敏 */}
                {member.phone && (
                  <div className="text-right flex-shrink-0">
                    <div className="text-xs text-gray-400">{maskPhone(member.phone)}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ====== 右下角悬浮按钮 ====== */}
      <button
        className="fixed bottom-8 right-5 w-13 h-13 rounded-full shadow-lg flex items-center justify-center cursor-pointer active:scale-95 transition-transform z-50"
        style={{ background: "linear-gradient(135deg,#FB923C,#EA580C)", width: 52, height: 52 }}
      >
        <i className="ri-customer-service-2-line text-white" style={{ fontSize: 24 }} />
      </button>
    </div>
  );
}

/** 格式化绑定时间：2026-01-15 10:30:00 → 2026-01-15 */
function formatDate(dateStr?: string): string {
  if (!dateStr) return "";
  return dateStr.split(" ")[0];
}

/** 手机号脱敏显示：138****1234 */
function maskPhone(phone?: string): string {
  if (!phone || phone.length < 7) return phone || "";
  return phone.slice(0, 3) + "****" + phone.slice(-4);
}
